import React from 'react';
import { Box, Button, Card, CardContent, Grid, Typography, Divider, useTheme, Fade } from '@mui/material';
import { BsArrowLeft } from 'react-icons/bs';
import { useNavigate, useLocation } from 'react-router';
import { motion } from 'framer-motion';

function BreakDown() {
  const navigate = useNavigate();
  const location = useLocation();
  const asanaData = location.state || {};
  const theme = useTheme();

  const handleBackToReport = () => {
    const roleid = localStorage.getItem('roleid');
    if (roleid === '5') {
      navigate('/staff-summary');
    } else {
      navigate('/summary');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.grey[50],
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 2, sm: 4, md: 8 }
      }}
    >
      {/* Back Button */}
      <Fade in timeout={800}>
        <Box display="flex" justifyContent="flex-end" mb={4}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BsArrowLeft />}
            onClick={handleBackToReport}
            sx={{
              borderRadius: 8,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.2,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Back to Summary
          </Button>
        </Box>
      </Fade>

      {/* Course & Asana Info Card */}
      <Fade in timeout={1000}>
        <Card
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            borderRadius: 3,
            bgcolor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            border: `1px solid ${theme.palette.primary.main}`
          }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{
              mb: 1,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              letterSpacing: 0.5
            }}
          >
            {asanaData.courseName || '-'}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={500}
            sx={{
              mb: 1,
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}
          >
            {asanaData.asanaName || '-'}
          </Typography>
          <Typography
            variant="subtitle1"
            fontStyle="italic"
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem' },
              opacity: 0.85
            }}
          >
            {asanaData.date
              ? new Date(asanaData.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })
              : '-'}
          </Typography>
        </Card>
      </Fade>

      {/* Performance Stats */}
      <Fade in timeout={1200}>
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.grey[200]}`
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: '1.5rem', sm: '1.8rem' }
              }}
            >
              Performance Summary
            </Typography>
            <Divider sx={{ mb: 3, borderColor: theme.palette.grey[300] }} />
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {[
                { label: 'Performance', value: `${asanaData.performance || 0}%` },
                { label: 'Duration (seconds)', value: asanaData.Duration || '-' },
                { label: 'Frame Count', value: asanaData.frameCount || '-' },
                { label: 'Good Posture Time (s)', value: asanaData.goodPostureTime || '-' }
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.2 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: theme.palette.grey[100],
                        border: `1px solid ${theme.palette.grey[300]}`,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          bgcolor: theme.palette.grey[200],
                          borderColor: theme.palette.primary.light
                        }
                      }}
                    >
                      <Typography
                        variant="h3"
                        fontWeight={700}
                        color="primary"
                        sx={{
                          mb: 1,
                          fontSize: { xs: '1.6rem', sm: '2rem' }
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          fontWeight: 500
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      {/* Feedback Section */}
      <Fade in timeout={1400}>
        <Card
          sx={{
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.grey[200]}`
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: '1.5rem', sm: '1.8rem' }
              }}
            >
              Feedback & Tips
            </Typography>
            <Divider sx={{ mb: 3, borderColor: theme.palette.grey[300] }} />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                lineHeight: 1.9,
                fontSize: { xs: '0.95rem', sm: '1.1rem' }
              }}
            >
              {asanaData.feedBack || 'No feedback provided for this session.'}
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
}

export default BreakDown;
